import { useEffect, useState } from 'react'
import { CourseSmilyGraphs } from './CourseSmilyGraphs'
import { XRHudSurface, useXRSession } from './XRHudSurface'
import { XR_HTML_PLACEMENT } from '../../core/extras/xrHtmlBridge'

const errors = {
  invalid_course_credentials: 'Utente o password non corretti.',
  invalid_login_fields: 'Inserisci utente e password.',
  course_login_unavailable: 'Connessione non disponibile. Riprova.',
  sign_in_required: 'Sign in again to use the Course Room.',
  easytech_session_expired: 'The Easy Tech session has expired. Retry to reconnect.',
  easytech_not_configured: 'The Easy Tech test connection is not configured.',
  easytech_role_denied: 'This Easy Tech account cannot change the selected room.',
  course_layout_invalid: 'The scene needs one valid course_layout marker.',
  station_model_missing: 'The smily model is unavailable.',
  provider_unavailable: 'Easy Tech is unavailable. Retry in a few seconds.',
  provider_busy: 'Easy Tech is busy. Retry in a few seconds.',
  provider_timeout: 'Easy Tech took too long to reply. Retry.',
  retry_later: 'Wait a few seconds before retrying.',
  invalid_provider_response: 'Easy Tech returned incomplete data.',
  room_denied: 'This account cannot access that room.',
  customer_denied: 'The Easy Tech customer does not match the test account.',
  test_account_retry_later: 'The test connection could not sign in. Retry in a few seconds.',
  no_course_rooms: 'No rooms are available for this Easy Tech account.',
}
const button = { padding: '10px 14px', borderRadius: 8, background: '#24364b', border: '1px solid #526477', color: 'white', font: 'inherit', cursor: 'pointer' }
// Number shown on the Smily panel in the scene (last number of its name), API id as fallback.
function smilyNumber(station) {
  return station.name?.match(/\d+(?!.*\d)/)?.[0] ?? station.id
}
export function CourseRoomPanel({ world }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [state, setState] = useState(() => ({ ...world.courseRoom.state }))
  const session = useXRSession(world)
  useEffect(() => {
    world.on('course-panel', setState)
    setState({ ...world.courseRoom.state })
    return () => world.off('course-panel', setState)
  }, [world])
  if (!state.enabled) return null
  // In XR the same elements are shown in the HUD: the button bottom right, panels in the centre.
  if (!state.active) return (
    <>
    {/* Desktop/mobile shortcut to the chart of the nearby Smily (same proximity as the E key). */}
    {!session && state.nearbyStation && (
      <button type='button' onClick={() => world.courseRoom.openGraph(state.nearbyStation.id)} style={{ ...button, position: 'absolute', right: '1rem', bottom: '14.25rem', zIndex: 35, pointerEvents: 'auto' }}>Smily {smilyNumber(state.nearbyStation)}</button>
    )}
    <XRHudSurface world={world} placement={XR_HTML_PLACEMENT.courseButton}>
    <button type='button' onClick={() => world.courseRoom.open()} style={{ ...button, position: 'absolute', right: '1rem', bottom: '11rem', zIndex: 35, pointerEvents: 'auto' }}>Course Room</button>
    </XRHudSurface>
    </>
  )
  if (state.graphStationId) return (
    <XRHudSurface world={world} placement={XR_HTML_PLACEMENT.center}>
    <section aria-label='Smily chart' style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'min(720px, calc(100% - 2rem))', maxHeight: 'calc(100% - 2rem)', overflowY: 'auto', boxSizing: 'border-box', padding: 20, borderRadius: 12, background: '#0b121d', color: 'white', zIndex: 50, pointerEvents: 'auto' }}>
      <button type='button' onClick={() => world.courseRoom.close()} style={{ ...button, float: 'right' }} aria-label='Close chart'>×</button>
      <CourseSmilyGraphs world={world} state={state} button={button} direct />
    </section>
    </XRHudSurface>
  )
  return (
    <XRHudSurface world={world} placement={XR_HTML_PLACEMENT.center}>
    <section aria-label='Course Room management' style={{ position: 'absolute', right: '1rem', top: '5rem', width: 'min(380px, calc(100% - 2rem))', maxHeight: 'calc(100% - 6rem)', overflowY: 'auto', padding: 20, borderRadius: 12, border: '1px solid #526477', background: 'rgba(11, 18, 29, 0.96)', color: 'white', zIndex: 50, pointerEvents: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <strong>Course Room</strong>
        <button type='button' aria-label='Close Course Room panel' onClick={() => world.courseRoom.close()} style={button}>×</button>
      </div>
      <p style={{ fontSize: 13, opacity: 0.8 }}>Temporary panel · Easy Tech test API{state.localTest ? ' · Unity test account' : ''}</p>
      <p style={{ fontSize: 13 }}>The selected room is shared with everyone in this Course Room.</p>
      {state.error && <p role='alert'>{errors[state.error] || 'The Course Room could not load. Retry to reconnect.'}</p>}
      {state.needsLogin ? <form onSubmit={event => {
        event.preventDefault()
        const submittedPassword = password
        setPassword('')
        world.courseRoom.login(username, submittedPassword)
      }}>
        <p>Accedi con le credenziali Easy Tech di test.</p>
        <label htmlFor='course-username'>Utente</label>
        <input id='course-username' name='username' autoComplete='username' value={username} onChange={event => setUsername(event.target.value)} required maxLength={256} style={{ ...button, display: 'block', width: '100%', boxSizing: 'border-box', margin: '8px 0 14px' }} />
        <label htmlFor='course-password'>Password</label>
        <input id='course-password' name='password' type='password' autoComplete='current-password' value={password} onChange={event => setPassword(event.target.value)} required maxLength={1024} style={{ ...button, display: 'block', width: '100%', boxSizing: 'border-box', margin: '8px 0 14px' }} />
        <button type='submit' disabled={state.busy} style={button}>{state.busy ? 'Connessione…' : 'Connetti'}</button>
      </form> : <>
      <label htmlFor='course-room-select'>Room</label>
      <select id='course-room-select' value={state.selected ?? ''} disabled={state.busy || !state.canSelect} onChange={event => world.courseRoom.select(Number(event.target.value))} style={{ display: 'block', marginTop: 8, width: '100%', ...button }}>
        <option value='' disabled>{state.busy ? 'Loading rooms…' : !state.rooms.length ? 'No rooms loaded' : 'Select a room'}</option>
        {state.rooms.map(room => <option key={room.id} value={room.id}>{room.name || `Room ${room.id}`}</option>)}
      </select>
      {!state.busy && !state.error && state.rooms.length > 0 && !state.canSelect && <p>This Easy Tech account can view the current room; a teacher or administrator must select it.</p>}
      <p aria-live='polite'>{state.busy ? 'Loading…' : `${state.count} smilies`}</p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type='button' disabled={state.busy} onClick={() => state.error || !state.rooms.length ? world.courseRoom.initialize() : world.courseRoom.poll(true)} style={button}>Refresh</button>
        <button type='button' onClick={() => world.courseRoom.close()} style={button}>Back to room</button>
      </div>
      <CourseSmilyGraphs world={world} state={state} button={button} />
      </>}
      <p style={{ fontSize: 13 }}>Alarm: <span style={{ color: '#43c824' }}>0 green</span> · <span style={{ color: '#ffe900' }}>1 yellow</span> · <span style={{ color: '#ff4545' }}>2 red</span></p>
    </section>
    </XRHudSurface>
  )
}
